import React, { useState, useEffect, useRef } from "react";
import {
  useGridApiRef,
  GridToolbar,
  GridLogicOperator,
  getGridStringOperators,
} from "@mui/x-data-grid-pro";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { decoratedFetch } from "../req_utils";
import ThemedDataGridPro from "./ThemedDataGridPro";
import { updatePipelineFilters } from "../reducers"; // adjust the path if necessary

const ProjectsTable = ({
  employee_first_name,
  employee_last_name,
  ignoreSavedPipelineFilters,
  searchTerm = "",
}) => {
  const darkThemeEnabled = useSelector((state) => state.darkThemeEnabled);
  const reduxFilterModel = useSelector((state) => state.pipeline_filters);
  const employee_branch_id = useSelector((state) => state.employee_branch_id);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const apiRef = useRef(null);

  // Define initial filters based on employee names:
  const initialFilters = [];
  if (employee_first_name && employee_last_name) {
    initialFilters.push(
      {
        id: 4,
        field: "assignee",
        operator: "contains",
        value: `${employee_first_name} ${employee_last_name}`,
      },
      { id: 1, field: "status", operator: "!=", value: "Archive" },
      { id: 2, field: "status", operator: "!=", value: "Job Complete" },
    );
  } else {
    initialFilters.push(
      { id: 1, field: "status", operator: "!=", value: "Archive" },
      { id: 2, field: "status", operator: "!=", value: "Job Complete" },
    );
  }

  // Use redux filter model if available, otherwise use the initial filters
  const initialFilterModel =
    !ignoreSavedPipelineFilters && reduxFilterModel && reduxFilterModel.items
      ? reduxFilterModel
      : { items: initialFilters, logicOperator: GridLogicOperator.And };

  const [filterModel, setFilterModel] = useState(initialFilterModel);

  // If the redux filter state changes (e.g. from another part of the app), update the local state.
  useEffect(() => {
    if (
      !ignoreSavedPipelineFilters &&
      reduxFilterModel &&
      reduxFilterModel.items
    ) {
      setFilterModel(reduxFilterModel);
    }
  }, [reduxFilterModel]);

  const customStringOperators = () => {
    const defaultStringOperators = getGridStringOperators();
    // Adding a custom operator for "Not Equals"
    const notEqualsOperator = {
      label: "not equals",
      value: "!=",
      getApplyFilterFn: (filterItem) => {
        return (params) => {
          return params !== filterItem.value;
        };
      },
      InputComponent: defaultStringOperators.find(
        (op) => op.value === "contains",
      ).InputComponent,
    };

    // Return the array of operators with the custom operator added
    return [...defaultStringOperators, notEqualsOperator];
  };

  const columns = [
    {
      field: "projectName",
      headerName: "Project Name",
      width: 390,
      minWidth: 200,
      editable: false,
      flex: 1,
    },
    {
      field: "status",
      headerName: "Status",
      width: 240,
      minWidth: 150,
      editable: false,
      filterOperators: customStringOperators(),
      flex: 0.8,
    },
    { 
      field: "assignee", 
      headerName: "Assignee", 
      width: 250,
      minWidth: 150,
      flex: 0.8,
    },
    { 
      field: "sales_person", 
      headerName: "Sales Person", 
      width: 250,
      minWidth: 150,
      flex: 0.8,
    },
    { 
      field: "pm", 
      headerName: "PM", 
      width: 250,
      minWidth: 150,
      flex: 0.8,
    },
    { 
      field: "consultant_name", 
      headerName: "Consultant", 
      width: 250,
      minWidth: 150,
      flex: 0.8,
    },
    { 
      field: "commissions_paid", 
      headerName: "Commissions Paid", 
      width: 200,
      minWidth: 150,
      flex: 0.6,
      renderCell: (params) => {
        const value = params.value;
        if (value === true) return "Yes";
        return "";
      },
    },
    {
      field: "cmas_type",
      headerName: "CMAS Type",
      width: 250,
      minWidth: 150,
      flex: 0.8,
    },
  ];

  useEffect(() => {
    if (!employee_branch_id) return;
    
    setLoadingProjects(true);
    decoratedFetch(`/list_projects?branch_id=${employee_branch_id}`)
      .then((response) => response.json())
      .then((data) => {
        const treeData = data.map((project) => ({
          id: project.id,
          projectName: project?.name,
          sales_person: project?.sales_employee_name,
          status: project?.status_name,
          assignee: project?.assignee_name,
          pm: project?.pm_employee_name,
          consultant_name: project?.consultant ? `${project.consultant.first_name} ${project.consultant.last_name}` : null,
          commissions_paid: project?.commissions_paid,
          cmas_type: project?.cmas_type,
          treeDataPath: [project?.account_name, project?.custom_id],
        }));

        setProjects(treeData);
        setLoadingProjects(false);
      })
      .catch((error) => {
        setLoadingProjects(false);
        console.error(`Error listing projects: ${error}`);
      });
  }, [employee_branch_id]);

  // Filter projects based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredProjects(projects);
    } else {
      const filtered = projects.filter((project) => {
        const projectName = project.projectName?.toLowerCase() || "";
        const accountName = project.treeDataPath?.[0]?.toLowerCase() || "";
        const customId = project.treeDataPath?.[1]?.toLowerCase() || "";
        const consultantName = project.consultant_name?.toLowerCase() || "";
        const cmasType = project.cmas_type?.toLowerCase() || "";
        const searchLower = searchTerm.toLowerCase();
        
        return projectName.includes(searchLower) || 
               accountName.includes(searchLower) || 
               customId.includes(searchLower) ||
               consultantName.includes(searchLower) ||
               cmasType.includes(searchLower);
      });
      setFilteredProjects(filtered);
    }
  }, [projects, searchTerm]);

  const handleRowClick = (params, event) => {
    // Check if the row is a group row (has children) by checking if treeDataPath is present and has multiple parts
    if (params.row.treeDataPath && params.row.treeDataPath.length < 1) {
      const rowNode = apiRef.current.getRowNode(params.id);
      apiRef.current.setRowChildrenExpansion(
        params.id,
        !rowNode.childrenExpanded,
      );
      // Allow default behavior for expansion/collapse of group rows
      return;
    } else if (!params.row.treeDataPath) {
      const rowNode = apiRef.current.getRowNode(params.id);
      apiRef.current.setRowChildrenExpansion(
        params.id,
        !rowNode.childrenExpanded,
      );
      // This is the top-level group which might not have a treeDataPath.
      return;
    } else {
      // Prevent the default behavior to avoid any unwanted navigation or actions
      event.stopPropagation();
      // Navigate if it's a regular row
      navigate(`/project/${params.id}`);
    }
  };

  return (
    <ThemedDataGridPro
      rows={filteredProjects}
      slots={{ toolbar: GridToolbar }}
      columns={columns}
      filterModel={filterModel}
      initialState={{
        filter: {
          filterModel: filterModel,
        },
        columns: {
          columnVisibilityModel: {
            consultant_name: false,
            commissions_paid: false,
            cmas_type: false,
          },
        },
      }}
      apiRef={apiRef}
      getTreeDataPath={(row) => row.treeDataPath}
      groupingMode="treeData"
      onFilterModelChange={(newModel) => {
        setFilterModel(newModel);
        dispatch(updatePipelineFilters(newModel));
      }}
      pageSize={5}
      onRowClick={(params, event) => handleRowClick(params, event)}
      localeText={{ noRowsLabel: searchTerm ? "No projects found" : "No projects" }}
      treeData
      setRowChildren
      loading={loadingProjects}
      groupingColDef={{
        headerName: "Account",
        width: 400,
        minWidth: 200,
        flex: 1,
      }}
      autosizeOnMount={true}
      disableAutosize={false}
      sx={{ 
        minHeight: "180px",
        height: "calc(100vh - 200px)",
      }}
    />
  );
};

export default ProjectsTable;
